import { apiService } from './api';
import { ApiResponse } from '../types';

export interface ClientAccessConfig {
  id: number;
  clientId: number;
  canViewOwnQuestionnaire: boolean;
  canViewReports: boolean;
  canViewDashboard: boolean;
  allowedResourceIds?: number[];
  createdAt: string;
  updatedAt: string;
}

export interface QuestionAccessConfig {
  id: number;
  questionId: string | number;
  clientId: number;
  assessmentId?: number;
  canView: boolean;
  canEdit: boolean;
  canViewResponse: boolean;
  isLocked?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const clientAccessService = {
  async getClientAccessConfig(clientId: number): Promise<ClientAccessConfig> {
    const response = await apiService.get<ApiResponse<{ config: ClientAccessConfig }>>(
      `/client-access/${clientId}`
    );
    if (response.data?.config) {
      return response.data.config;
    }
    throw new Error(response.message || 'Failed to fetch client access config');
  },

  async updateClientAccessConfig(
    clientId: number,
    data: Partial<ClientAccessConfig>
  ): Promise<ClientAccessConfig> {
    const response = await apiService.put<ApiResponse<{ config: ClientAccessConfig }>>(
      `/client-access/${clientId}`,
      data
    );
    if (response.data?.config) {
      return response.data.config;
    }
    throw new Error(response.message || 'Failed to update client access config');
  },

  async getQuestionAccessConfigs(
    clientId: number,
    assessmentId: number
  ): Promise<QuestionAccessConfig[]> {
    const response = await apiService.get<ApiResponse<{ configs: QuestionAccessConfig[] }>>(
      `/question-access/${clientId}/${assessmentId}`
    );
    if (response.data?.configs) {
      return response.data.configs;
    }
    throw new Error(response.message || 'Failed to fetch question access configs');
  },

  async getQuestionAccessConfig(
    clientId: number,
    assessmentId: number,
    questionId: string | number
  ): Promise<QuestionAccessConfig> {
    const response = await apiService.get<ApiResponse<{ config: QuestionAccessConfig }>>(
      `/question-access/${clientId}/${assessmentId}/${questionId}`
    );
    if (response.data?.config) {
      return response.data.config;
    }
    // Return default config if not found (API returns default when config doesn't exist)
    return {
      id: 0,
      questionId,
      clientId,
      assessmentId,
      canView: true,
      canEdit: false,
      canViewResponse: true,
      isLocked: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  },

  async updateQuestionAccessConfig(
    clientId: number,
    assessmentId: number,
    questionId: string | number,
    data: Partial<QuestionAccessConfig>
  ): Promise<QuestionAccessConfig> {
    const response = await apiService.put<ApiResponse<{ config: QuestionAccessConfig }>>(
      `/question-access/${clientId}/${assessmentId}/${questionId}`,
      data
    );
    if (response.data?.config) {
      return response.data.config;
    }
    throw new Error(response.message || 'Failed to update question access config');
  },

  async bulkUpdateQuestionAccessConfigs(
    clientId: number,
    assessmentId: number,
    configs: Array<{ questionId: string | number; canView?: boolean; canEdit?: boolean; canViewResponse?: boolean; isLocked?: boolean }>
  ): Promise<QuestionAccessConfig[]> {
    const response = await apiService.put<ApiResponse<{ configs: QuestionAccessConfig[] }>>(
      `/question-access/${clientId}/${assessmentId}`,
      { configs }
    );
    if (response.data?.configs) {
      return response.data.configs;
    }
    throw new Error(response.message || 'Failed to update question access configs');
  }
};

